<template>
  <div
    class="relative space-y-2 border-white/10"
    :class="sidebarExpanded ? 'justify-left p-2 pb-3' : 'justify-center'"
  >
    <router-link
      v-tooltip="{
        content: $t('SIDEBAR.PROFILE.TITLE'),
        onShow: () => !sidebarExpanded,
      }"
      :to="{ name: ROUTE_ACCOUNT }"
      :class="
        alreadyImpersonatingSomeone
          ? 'bg-[#9EC6F2] text-login-bg'
          : 'bg-login-bg text-surface-user-blue'
      "
      class="group/sidebar relative flex max-w-full cursor-pointer justify-center overflow-hidden rounded-sm py-2 focus:outline-hidden"
      data-cy="impersonate-block-icon"
    >
      <icon-base
        :class="[
          sidebarExpanded ? 'ml-2' : '',
          !alreadyImpersonatingSomeone ? 'opacity-50' : '',
        ]"
        class="shrink-0 basis-6 place-self-center"
        height="24"
        width="24"
        view-box="0 0 48 48"
        :icon="IconProfile"
      />

      <div
        v-if="sidebarExpanded"
        data-cy="impersonate-block-data"
        class="place-self-cente grow overflow-hidden px-4 text-sm whitespace-nowrap transition-colors duration-200"
      >
        <div class="flex shrink flex-col justify-between space-y-1 font-medium">
          <span
            v-tooltip="fullUserName"
            :class="!alreadyImpersonatingSomeone ? 'opacity-80' : ''"
            class="truncate"
            data-cy="impersonate-block-name"
          >
            {{ fullUserName }}
          </span>
          <span
            v-tooltip="{
              content: userRoles,
              onShow: () => !!user.roles.length,
            }"
            class="truncate text-xs font-normal opacity-55"
            data-cy="impersonate-block-role"
          >
            {{ userRoles }}
          </span>
        </div>
      </div>
    </router-link>
    <div
      v-if="impersonateModalOpen"
      v-on-click-outside="toggleModal"
      class="relative"
    >
      <impersonate-modal
        class="absolute bottom-4"
        @impersonated="impersonateUser"
      />
    </div>
    <div
      v-if="sidebarExpanded && (isAdmin || alreadyImpersonatingSomeone)"
      :class="[
        alreadyImpersonatingSomeone
          ? 'bg-surface-user-blue/25'
          : 'bg-[#9EC6F2]/10',
        impersonateModalOpen ? 'bg-[#9EC6F2]! text-login-bg' : 'text-[#9DC4F0]',
      ]"
      class="cursor-pointer rounded-5 p-1 text-center text-xs font-semibold uppercase"
    >
      <div
        v-if="alreadyImpersonatingSomeone"
        class="text-surface-user-blue"
        data-cy="impersonate-block-exit-button"
        @click="stopImpersonating"
      >
        {{ $t("COMMON.EXIT") }}
      </div>
      <div
        v-else
        data-cy="impersonate-block-select-button"
        @click.self="toggleModal"
      >
        {{ t("COMMON.IMPERSONATE") }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import startCase from "lodash/startCase";

import { ADMIN, SUPER_ADMIN } from "@/helpers/constants";
import { formatUserFullName, getUserRoles } from "@/helpers/formatting";
import { useImpersonate } from "@/hooks/auth";
import { ROUTE_ACCOUNT } from "@/router/routes";

import IconProfile from "@/components/icons/sidebar/IconProfile.vue";
import ImpersonateModal from "@/components/sidebar/ImpersonateModal.vue";

import type { IUser } from "@/models/users";

const { sidebarExpanded = false } = defineProps<{
  sidebarExpanded: boolean;
}>();

const { getters, dispatch, commit } = useStore();
const { t } = useI18n();
const { alreadyImpersonatingSomeone, setImpersonateMessage } = useImpersonate();

const impersonateModalOpen = ref(false);

const user = computed<IUser>(() => getters["auth/user"]);

const userRoles = computed(() =>
  startCase(getUserRoles(user.value.roles)?.join(", "))
);

const isAdmin = computed(
  () =>
    user.value?.roles.includes(ADMIN) || user.value?.roles.includes(SUPER_ADMIN)
);

const fullUserName = computed(() => formatUserFullName(user.value));

const toggleModal = () => {
  impersonateModalOpen.value = !impersonateModalOpen.value;
};

const stopImpersonating = async () => {
  localStorage.setItem("impersonationInProgress", "true");
  await dispatch("auth/stopImpersonating", { canRedirect: false });
  setImpersonateMessage();
  window.location.assign(window.location.pathname);
  commit("resetHistoryStack", null, { root: true });
};

const impersonateUser = () => {
  localStorage.setItem("impersonationInProgress", "true");
  setImpersonateMessage();
  impersonateModalOpen.value = false;
  window.location.assign(window.location.pathname);
};
</script>
